import { NOTIFICATION_CATEGORIES, NOTIFICATION_CATEGORY_KEYS, type NotificationCategory, type NotificationPrefs, type NotificationSettingsDto } from '@edms/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Icon } from '../lib/icons';
import { disablePush, enablePush, isIos, isStandalone, pushState, useInstallPrompt, type PushState } from '../lib/push';
import { formatDate, useToast } from '../lib/ui';

/** Состояние push на этом устройстве; refresh — перечитать после включения или выключения. */
export function usePushState(): { state: PushState | null; refresh: () => void } {
  const [state, setState] = useState<PushState | null>(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let alive = true;
    void pushState().then((s) => alive && setState(s));
    return () => {
      alive = false;
    };
  }, [tick]);
  return { state, refresh: () => setTick((n) => n + 1) };
}

/** Кнопка «Включить уведомления»: разрешение браузера и подписка этого устройства. */
export function useEnablePush(onDone: () => void) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: enablePush,
    onSuccess: () => {
      toast('Уведомления включены — так они будут приходить на это устройство');
      void qc.invalidateQueries({ queryKey: ['notification-settings'] });
      onDone();
      // Сразу показать, как выглядит уведомление.
      void api.post('/notifications/push/test').catch(() => undefined);
    },
    onError: (e) => {
      toast(`Не удалось включить: ${e.message}`, { error: true });
      onDone();
    },
  });
}

const DEVICE_HINT: Record<PushState, string> = {
  on: 'Уведомления приходят на это устройство, даже когда сайт закрыт: на телефоне — в шторку, на компьютере — в центр уведомлений.',
  off: 'Включите, чтобы задачи на согласование, поручения и напоминания приходили на это устройство, как сообщения мессенджера.',
  denied:
    'Уведомления запрещены в настройках браузера. Разрешите их: значок замка (или «i») слева от адреса сайта → «Уведомления» → «Разрешить», затем обновите страницу.',
  'ios-install':
    'На iPhone и iPad уведомления работают в установленном приложении: в Safari нажмите «Поделиться» → «На экран «Домой»», откройте EDMS с экрана Домой и включите уведомления здесь.',
  unsupported: 'Этот браузер не поддерживает уведомления. Подойдут Chrome, Edge, Яндекс Браузер, Firefox или Safari.',
};

/** Блок «Уведомления» в профиле: это устройство, установка приложения, устройства, категории. */
export function NotificationSettingsCard() {
  const qc = useQueryClient();
  const toast = useToast();
  const settings = useQuery({ queryKey: ['notification-settings'], queryFn: () => api.get<NotificationSettingsDto>('/notifications/settings') });
  const { state, refresh } = usePushState();
  const enable = useEnablePush(refresh);
  const disable = useMutation({
    mutationFn: disablePush,
    onSuccess: () => {
      toast('Уведомления на этом устройстве выключены');
      void qc.invalidateQueries({ queryKey: ['notification-settings'] });
      refresh();
    },
  });
  const test = useMutation({
    mutationFn: () => api.post<{ sent: number; failed: number }>('/notifications/push/test'),
    onSuccess: (r) => toast(r.sent ? `Отправлено на устройств: ${r.sent}. Проверьте шторку или центр уведомлений.` : 'Нет устройств, куда отправить', { error: !r.sent }),
  });
  const removeDevice = useMutation({
    mutationFn: (id: string) => api.delete(`/notifications/push/devices/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notification-settings'] });
      refresh();
    },
  });
  const save = useMutation({
    mutationFn: (prefs: NotificationPrefs) => api.patch('/notifications/settings', prefs),
    onError: (e) => {
      toast(e.message, { error: true });
      void qc.invalidateQueries({ queryKey: ['notification-settings'] });
    },
  });
  const install = useInstallPrompt();
  // Ссылка «Настроить» со страницы уведомлений ведёт сюда: /profile#notifications.
  useEffect(() => {
    if (window.location.hash === '#notifications') document.getElementById('notifications')?.scrollIntoView({ block: 'start' });
  }, [settings.isSuccess]);

  const prefs = settings.data?.prefs;
  const toggle = (channel: 'push' | 'email', cat: NotificationCategory, on: boolean) => {
    if (!prefs) return;
    const next = { ...prefs[channel] };
    if (on) delete next[cat];
    else next[cat] = false;
    const updated = { ...prefs, [channel]: next };
    // Галочка меняется сразу, сохранение — следом; при ошибке настройки перечитаются.
    qc.setQueryData<NotificationSettingsDto>(['notification-settings'], (s) => (s ? { ...s, prefs: updated } : s));
    save.mutate(updated);
  };

  return (
    <div className="card card-pad notify-settings" id="notifications">
      <div>
        <h2>Уведомления</h2>
        <p className="faint">В колокольчике на сайте видно всё. Здесь — что приходит на телефон и компьютер и что письмом.</p>
      </div>

      <section className="notify-device">
        <div className="notify-device-head">
          <span className={`notify-dot ${state === 'on' ? 'on' : ''}`} aria-hidden />
          <b>{state === 'on' ? 'На этом устройстве включены' : 'На этом устройстве'}</b>
        </div>
        {state && <p className="faint">{DEVICE_HINT[state]}</p>}
        <div className="notify-actions">
          {state === 'off' && (
            <button className="btn btn-primary" disabled={enable.isPending} onClick={() => enable.mutate()}>
              <Icon name="bell" size={18} /> {enable.isPending ? 'Включаем…' : 'Включить уведомления'}
            </button>
          )}
          {state === 'on' && (
            <>
              <button className="btn" disabled={test.isPending} onClick={() => test.mutate()}>
                Отправить проверочное
              </button>
              <button className="btn btn-ghost" disabled={disable.isPending} onClick={() => disable.mutate()}>
                Выключить на этом устройстве
              </button>
            </>
          )}
          {install.canInstall && (
            <button className="btn" onClick={() => void install.install()}>
              <Icon name="download" size={18} /> Установить приложение
            </button>
          )}
        </div>
        {isStandalone() ? (
          <p className="faint">EDMS открыт как приложение — с иконкой на экране и числом непрочитанных на ней.</p>
        ) : (
          !install.canInstall &&
          !isIos() && (
            <p className="faint">
              Установить EDMS как приложение: меню браузера (⋮) → «Установить приложение» или «Добавить на главный экран». Уведомления будут приходить с иконкой
              EDMS, как от мессенджеров.
            </p>
          )
        )}
      </section>

      {settings.data && settings.data.devices.length > 0 && (
        <section>
          <b>Мои устройства</b>
          <ul className="notify-devices">
            {settings.data.devices.map((d) => (
              <li key={d.id}>
                <span>
                  {d.label}
                  <span className="faint"> · с {formatDate(d.createdAt)}{d.lastSuccessAt ? `, последнее — ${formatDate(d.lastSuccessAt)}` : ''}</span>
                </span>
                <button className="btn btn-ghost btn-sm" onClick={() => removeDevice.mutate(d.id)} disabled={removeDevice.isPending}>
                  Отключить
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {prefs && (
        <section>
          <div className="table-wrap">
            <table className="table notify-matrix">
              <thead>
                <tr>
                  <th>Что</th>
                  <th title="На телефон и компьютер, где включены уведомления">Устройства</th>
                  <th>Письмо</th>
                </tr>
              </thead>
              <tbody>
                {NOTIFICATION_CATEGORY_KEYS.map((cat) => (
                  <tr key={cat}>
                    <td>
                      {NOTIFICATION_CATEGORIES[cat].label}
                      <div className="faint">{NOTIFICATION_CATEGORIES[cat].hint}</div>
                    </td>
                    <td>
                      <input type="checkbox" aria-label={`${NOTIFICATION_CATEGORIES[cat].label}: на устройства`} checked={prefs.push[cat] !== false} onChange={(e) => toggle('push', cat, e.target.checked)} />
                    </td>
                    <td>
                      {NOTIFICATION_CATEGORIES[cat].noEmail ? (
                        <span className="faint" title="По этой категории письма не отправляются">—</span>
                      ) : (
                        <input type="checkbox" aria-label={`${NOTIFICATION_CATEGORIES[cat].label}: письмом`} checked={prefs.email[cat] !== false} onChange={(e) => toggle('email', cat, e.target.checked)} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="faint">Письмом приходят только важные события — какие именно, решает система; здесь можно отключить ненужные. Рассылки администратора письмом получают все.</p>
        </section>
      )}
    </div>
  );
}

const BANNER_KEY = 'edms-push-banner';

/**
 * Предложение на главной включить уведомления на этом устройстве — пока они выключены
 * и сотрудник не отказался («Не сейчас» прячет на 30 дней).
 */
export function PushBanner() {
  const { state, refresh } = usePushState();
  const enable = useEnablePush(refresh);
  const [hidden, setHidden] = useState(() => {
    try {
      return Number(localStorage.getItem(BANNER_KEY) ?? 0) > Date.now();
    } catch {
      return false;
    }
  });
  if (hidden || (state !== 'off' && state !== 'ios-install')) return null;
  const later = () => {
    try {
      localStorage.setItem(BANNER_KEY, String(Date.now() + 30 * 86_400_000));
    } catch {
      // без сохранения — просто прячем до перезагрузки
    }
    setHidden(true);
  };
  return (
    <div className="card card-pad push-banner">
      <span className="push-banner-icon" aria-hidden>
        <Icon name="bell" />
      </span>
      <div>
        <b>Получайте задачи сразу, как сообщения</b>
        <p className="faint">
          {state === 'ios-install'
            ? 'На iPhone: «Поделиться» → «На экран «Домой»», откройте EDMS с экрана Домой — и включите уведомления в профиле.'
            : 'Документы на согласование, поручения и напоминания будут приходить на это устройство, даже когда сайт закрыт.'}
        </p>
      </div>
      <span className="push-banner-actions">
        {state === 'off' && (
          <button className="btn btn-primary" disabled={enable.isPending} onClick={() => enable.mutate()}>
            Включить
          </button>
        )}
        <button className="btn btn-ghost" onClick={later}>
          Не сейчас
        </button>
      </span>
    </div>
  );
}
